import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import {
  Award, Search, Star, Trash2, RefreshCw, Sparkles, X, Eye, Phone, Download, Copy,
  CheckCircle2, XCircle, AlertCircle, Printer, Users, Settings2
} from "lucide-react";

// Ícono de WhatsApp (lucide no lo incluye)
const WhatsAppIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.6-.4zM12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.3 5L2 22l5.1-1.3c1.5.8 3.1 1.3 4.9 1.3 5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3C4.2 15 3.8 13.5 3.8 12c0-4.5 3.7-8.2 8.2-8.2s8.2 3.7 8.2 8.2-3.7 8.2-8.2 8.2z"/>
  </svg>
);

// ── Mensaje y helpers de WhatsApp ──────────────────────────────────────────────
const nombrePila = (nombreCompleto) => {
  const n = (nombreCompleto || '').trim().split(/\s+/)[0] || '';
  return n.charAt(0).toUpperCase() + n.slice(1).toLowerCase();
};

export const linkCarnetPublico = (carnet) => `${window.location.origin}/carnet/${carnet.numero_serie}`;

export const mensajeWhatsApp = (carnet) => {
  const [y, m, d] = (carnet.fecha_vencimiento || '').split('-');
  return (
`Hola ${nombrePila(carnet.nombre_completo)} 👋

¡Has sido seleccionado(a) para formar parte de los *Clientes VIP de Farmavida*! 🌟

Por tu preferencia y fidelidad, desde hoy disfrutas de un *${carnet.porcentaje_descuento}% de descuento en todas tus compras* en cualquiera de nuestras sedes.

🪪 Tu carnet digital: ${linkCarnetPublico(carnet)}

Presenta esta imagen o tu cédula en caja para aplicar tu descuento. Vigente hasta el ${d}/${m}/${y}.

¡Gracias por preferirnos! 💊
_Farmavida · Nos encanta tenerte como Cliente VIP_`
  );
};

const telWhatsApp = (tel) => {
  const d = String(tel || '').replace(/\D/g, '');
  if (!d) return '';
  return d.startsWith('58') ? d : '58' + d.replace(/^0/, '');
};

const esMovil = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

export const copiarMensaje = async (carnet) => {
  const texto = mensajeWhatsApp(carnet);
  try {
    await navigator.clipboard.writeText(texto);
  } catch {
    // Respaldo para navegadores sin API de portapapeles
    const ta = document.createElement('textarea');
    ta.value = texto;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
  toast.success(`Mensaje copiado · pégalo en el chat de ${nombrePila(carnet.nombre_completo)}${carnet.telefono ? ` (${fmtTel(carnet.telefono)})` : ''}`);
};

export const abrirWhatsApp = (carnet) => {
  const tel = telWhatsApp(carnet.telefono);
  const texto = encodeURIComponent(mensajeWhatsApp(carnet));
  // En computadora abrimos WhatsApp Web directamente: el salto de wa.me a la app de
  // escritorio (Mac/Windows) corrompe los emojis del texto. En celular wa.me funciona bien.
  const url = esMovil()
    ? `https://wa.me/${tel}?text=${texto}`
    : `https://web.whatsapp.com/send?phone=${tel}&text=${texto}`;
  window.open(url, '_blank', 'noopener');
};
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { API } from "@/components/App";

// ── Code 39 barcode renderer (real specification) ──────────────────────────────
const CODE39 = {
  '*': '010010100', // start/stop
  '0': '000110100', '1': '100100001', '2': '001100001', '3': '101100000',
  '4': '000110001', '5': '100110000', '6': '001110000', '7': '000100101',
  '8': '100100100', '9': '001100100',
  'A': '100001001', 'B': '001001001', 'C': '101001000', 'D': '000011001',
  'E': '100011000', 'F': '001011000', 'G': '000001101', 'H': '100001100',
  'I': '001001100', 'J': '000011100', 'K': '100000011', 'L': '001000011',
  'M': '101000010', 'N': '000010011', 'O': '100010010', 'P': '001010010',
  'Q': '000000111', 'R': '100000110', 'S': '001000110', 'T': '000010110',
  'U': '110000001', 'V': '011000001', 'W': '111000000', 'X': '010001001',
  'Y': '110001000', 'Z': '011001000', '-': '010010010',
};

function Barcode({ value = '', height = 48, color = '#1a0a0a' }) {
  const NARROW = 1.6;
  const WIDE   = 4.0;
  const INTER  = NARROW; // inter-character gap (narrow white)

  const chars = ['*', ...value.toUpperCase().split(''), '*'];
  const elements = [];

  chars.forEach((ch, ci) => {
    const pat = CODE39[ch] || CODE39['0'];
    for (let i = 0; i < 9; i++) {
      elements.push({ w: pat[i] === '1' ? WIDE : NARROW, isBar: i % 2 === 0 });
    }
    if (ci < chars.length - 1) elements.push({ w: INTER, isBar: false });
  });

  const totalW = elements.reduce((s, e) => s + e.w, 0);
  let x = 0;
  const rects = [];
  elements.forEach((el, i) => {
    if (el.isBar) rects.push(<rect key={i} x={x} y={0} width={el.w} height={height} fill={color} />);
    x += el.w;
  });

  return (
    <svg width={totalW} height={height} viewBox={`0 0 ${totalW} ${height}`} style={{ display: 'block' }}>
      {rects}
    </svg>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const fmtTel = (t) => {
  if (!t) return '—';
  const d = String(t).replace(/\D/g, '');
  return d.length === 11 ? `${d.slice(0, 4)}-${d.slice(4, 7)}.${d.slice(7)}` : d;
};

const isVencido = (fechaIso) => {
  if (!fechaIso) return false;
  return fechaIso < new Date().toISOString().slice(0, 10);
};

// ── VIP Card visual ────────────────────────────────────────────────────────────
export function VIPCard({ carnet, showPrint = true, showDownload = true, showWhatsApp = false }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const generarPng = async () => {
    const el = cardRef.current;
    if (!el) return null;
    const dataUrl = await toPng(el, { pixelRatio: 3, cacheBust: true });
    return dataUrl;
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const dataUrl = await generarPng();
      if (!dataUrl) return;
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `Carnet-VIP-${carnet.numero_serie}.png`;
      a.click();
    } catch {
      toast.error('No se pudo generar la imagen');
    } finally {
      setBusy(false);
    }
  };

  const handleWhatsApp = async () => {
    setBusy(true);
    try {
      // En celular: compartir la imagen + texto directo a WhatsApp
      if (navigator.canShare) {
        const dataUrl = await generarPng();
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], `Carnet-VIP-${carnet.numero_serie}.png`, { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: mensajeWhatsApp(carnet) });
          return;
        }
      }
      // En escritorio: abrir WhatsApp con el texto y el enlace al carnet
      abrirWhatsApp(carnet);
    } catch (e) {
      if (e?.name !== 'AbortError') abrirWhatsApp(carnet);
    } finally {
      setBusy(false);
    }
  };

  const handlePrint = () => {
    const el = cardRef.current;
    if (!el) return;
    const w = window.open('', '', 'width=900,height=600');
    w.document.write(`
      <!doctype html><html><head>
      <meta charset="utf-8">
      <title>Carnet VIP — ${carnet.nombre_completo}</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { background:#f5f5f5; display:flex; align-items:center; justify-content:center; min-height:100vh; font-family:'Segoe UI',Arial,sans-serif; }
        .card { width:420px; padding:0; border-radius:18px; overflow:hidden; box-shadow:0 20px 60px rgba(0,0,0,.4); }
        @media print { body { background:white; } .card { box-shadow:none; } }
      </style>
      </head><body>
      ${el.outerHTML}
      <script>window.onload=()=>{window.print();window.close();}<\/script>
      </body></html>
    `);
    w.document.close();
  };

  const vence = isVencido(carnet.fecha_vencimiento);

  return (
    <div className="flex flex-col items-center gap-4">
      {/* ── Card ── */}
      <div
        ref={cardRef}
        style={{
          width: 420,
          background: 'linear-gradient(135deg, #5c0a0a 0%, #9b1515 45%, #7a0e0e 100%)',
          borderRadius: 18,
          overflow: 'hidden',
          boxShadow: '0 16px 48px rgba(0,0,0,.35)',
          fontFamily: "'Segoe UI', Arial, sans-serif",
          color: '#fff',
          position: 'relative',
        }}
      >
        {/* Subtle shine overlay */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 120,
          background: 'linear-gradient(180deg, rgba(255,255,255,.08) 0%, transparent 100%)',
          pointerEvents: 'none',
        }} />

        {/* Top strip */}
        <div style={{
          background: 'linear-gradient(90deg, #d4af37 0%, #f5d060 50%, #d4af37 100%)',
          height: 6,
        }} />

        {/* Header */}
        <div style={{ padding: '18px 22px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 42, height: 42, borderRadius: '50%',
              background: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,.35)',
              overflow: 'hidden', flexShrink: 0,
            }}>
              <img
                src={`${window.location.origin}/logo-farmavida2.jpg`}
                alt="Farmavida"
                style={{ width: 42, height: 42, objectFit: 'cover', display: 'block' }}
              />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1.5, color: '#f5d060' }}>FARMAVIDA</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,.65)', letterSpacing: 0.5 }}>Ahorra con Nosotros</div>
            </div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #d4af37, #f5d060)',
            borderRadius: 20,
            padding: '4px 12px',
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <span style={{ fontSize: 11, color: '#5c0a0a', fontWeight: 800, letterSpacing: 1 }}>★ CLIENTE VIP ★</span>
          </div>
        </div>

        {/* Discount badge */}
        <div style={{ padding: '0 22px 4px', display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{
            background: 'rgba(255,255,255,.12)',
            border: '1px solid rgba(212,175,55,.5)',
            borderRadius: 8,
            padding: '3px 10px',
            fontSize: 11,
            color: '#f5d060',
            fontWeight: 600,
          }}>
            {carnet.porcentaje_descuento}% de descuento en todas tus compras
          </div>
        </div>

        {/* Customer info */}
        <div style={{ padding: '10px 22px 6px' }}>
          <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', lineHeight: 1.2 }}>
            {carnet.nombre_completo}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,.7)', marginTop: 4 }}>
            Cédula: {carnet.cedula}
          </div>
        </div>

        {/* Barcode area */}
        <div style={{
          margin: '12px 22px 8px',
          background: '#fff',
          borderRadius: 8,
          padding: '10px 14px 6px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          overflow: 'hidden',
        }}>
          <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Barcode value={carnet.numero_serie} height={46} color="#1a0505" />
          </div>
          <div style={{
            fontSize: 10,
            color: '#555',
            letterSpacing: 2.5,
            fontFamily: 'monospace',
            fontWeight: 600,
          }}>
            {carnet.numero_serie}
          </div>
        </div>

        {/* Dates */}
        <div style={{
          padding: '6px 22px 10px',
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          color: 'rgba(255,255,255,.8)',
        }}>
          <div>
            <div style={{ fontSize: 8, color: 'rgba(255,255,255,.5)', letterSpacing: 0.8 }}>EMISIÓN</div>
            <div style={{ fontWeight: 600 }}>{fmt(carnet.fecha_emision)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 8, color: 'rgba(255,255,255,.5)', letterSpacing: 0.8 }}>VENCIMIENTO</div>
            <div style={{ fontWeight: 600, color: vence ? '#ff8080' : '#f5d060' }}>
              {fmt(carnet.fecha_vencimiento)}{vence ? ' ✗' : ''}
            </div>
          </div>
        </div>

        {/* Bottom strip + message */}
        <div style={{
          background: 'linear-gradient(90deg, #d4af37 0%, #f5d060 50%, #d4af37 100%)',
          padding: '8px 22px',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: 10, color: '#5c0a0a', fontWeight: 700, lineHeight: 1.5 }}>
            Agradecemos tu preferencia · Nos encanta tenerte como Cliente VIP 💊
          </p>
        </div>
      </div>

      {/* Acciones */}
      <div className="flex flex-wrap justify-center gap-2">
        {showWhatsApp && (
          <>
            <Button
              size="sm"
              className="gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white"
              onClick={() => copiarMensaje(carnet)}
              title="Copiar el mensaje de bienvenida VIP para pegarlo en WhatsApp"
            >
              <Copy className="w-4 h-4" />
              Copiar mensaje
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              className="gap-2 border-[#25D366] text-[#1ebe5b] hover:bg-green-50"
              onClick={handleWhatsApp}
              title={carnet.telefono ? `Abrir WhatsApp con ${fmtTel(carnet.telefono)}` : 'Abrir WhatsApp'}
            >
              <WhatsAppIcon />
              Abrir WhatsApp
            </Button>
          </>
        )}
        {showDownload && (
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            className="gap-2 border-red-200 text-red-700 hover:bg-red-50"
            onClick={handleDownload}
          >
            <Download className="w-4 h-4" />
            Descargar imagen
          </Button>
        )}
        {showPrint && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2 border-slate-200 text-slate-600 hover:bg-slate-50"
            onClick={handlePrint}
          >
            <Printer className="w-4 h-4" />
            Imprimir / PDF
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Frequency meter ────────────────────────────────────────────────────────────
function FrequencyMeter({ compras, minimas }) {
  const pct = Math.min(100, Math.round((compras / minimas) * 100));
  const color = compras >= minimas ? '#16a34a' : compras >= minimas * 0.6 ? '#d97706' : '#dc2626';
  return (
    <div className="w-full">
      <div className="flex justify-between text-xs text-slate-500 mb-1">
        <span>Compras en los últimos 30 días (todas las sedes)</span>
        <span className="font-semibold" style={{ color }}>
          {compras} / {minimas}
        </span>
      </div>
      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <div className="text-xs mt-1" style={{ color }}>
        {compras >= minimas
          ? '✓ ¡Califica para el carnet VIP!'
          : `Faltan ${minimas - compras} compra(s) para calificar`}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function CarnetVIP() {
  const [tab, setTab] = useState('consultar');
  const [cedula, setCedula] = useState('');
  const [searching, setSearching] = useState(false);
  const [frecResult, setFrecResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [allCarnets, setAllCarnets] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [config, setConfig] = useState({ compras_minimas_mes: 10, porcentaje_descuento: 7, meses_vigencia: 12, codigos_excluidos: [] });
  const [excluidosTexto, setExcluidosTexto] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  // Escaneo masivo de candidatos
  const [scan, setScan] = useState(null);           // resultado del escaneo
  const [scanning, setScanning] = useState(false);
  const [generandoCed, setGenerandoCed] = useState(new Set());
  const [generandoTodos, setGenerandoTodos] = useState(false);
  const [preview, setPreview] = useState(null);     // carnet a mostrar en modal
  const [actualizandoTel, setActualizandoTel] = useState(false);
  const [me, setMe] = useState(null);   // usuario actual (para permiso de excepción)

  useEffect(() => {
    axios.get(`${API}/auth/me`).then(r => setMe(r.data)).catch(() => setMe(null));
  }, []);

  const generarPorExcepcion = async () => {
    const motivo = window.prompt(
      `${frecResult.cliente_nombre} tiene ${frecResult.compras_mes} compras y el mínimo es ${frecResult.compras_minimas_mes}.\n\n` +
      `Indica el motivo de la excepción (quedará registrado con tu usuario):`
    );
    if (motivo === null) return;
    if (!motivo.trim()) { toast.error('Debes indicar un motivo'); return; }
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/vip/generar/${cedula.trim()}`, null, {
        params: { forzar: true, motivo: motivo.trim() },
      });
      setFrecResult(prev => ({ ...prev, tiene_carnet_activo: true, carnet: data }));
      toast.success('Carnet VIP generado por excepción');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error generando carnet');
    } finally {
      setGenerating(false);
    }
  };

  const actualizarTelefonos = async () => {
    setActualizandoTel(true);
    try {
      const { data } = await axios.post(`${API}/vip/carnets/actualizar-telefonos`);
      toast.success(`${data.actualizados} teléfono(s) completados${data.pendientes ? ` · ${data.pendientes} sin teléfono en Tecnodroid` : ''}`);
      cargarCarnets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error consultando teléfonos');
    } finally {
      setActualizandoTel(false);
    }
  };

  const escanear = async () => {
    setScanning(true);
    try {
      const { data } = await axios.get(`${API}/vip/candidatos`);
      setScan(data);
      if (data.candidatos.length === 0) toast.info('Ningún cliente alcanza el mínimo en los últimos 30 días');
      else toast.success(`${data.candidatos.length} cliente(s) califican para el carnet VIP`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error escaneando el Tecnodroid');
    } finally {
      setScanning(false);
    }
  };

  const generarPara = async (cand, { silent = false } = {}) => {
    setGenerandoCed(prev => new Set(prev).add(cand.cedula));
    try {
      const { data } = await axios.post(`${API}/vip/generar/${cand.cedula}`, null, {
        params: { nombre: cand.nombre || undefined, compras: cand.compras, telefono: cand.telefono || undefined },
      });
      setScan(prev => prev && ({
        ...prev,
        candidatos: prev.candidatos.map(c =>
          c.cedula === cand.cedula ? { ...c, tiene_carnet_activo: true, carnet: data } : c
        ),
      }));
      if (!silent) { setPreview(data); toast.success(`Carnet generado para ${data.nombre_completo}`); }
      return true;
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || 'Error generando carnet');
      return false;
    } finally {
      setGenerandoCed(prev => { const s = new Set(prev); s.delete(cand.cedula); return s; });
    }
  };

  const generarTodos = async () => {
    const pendientes = (scan?.candidatos || []).filter(c => !c.tiene_carnet_activo && c.nombre);
    if (pendientes.length === 0) return;
    if (!window.confirm(`¿Generar carnet VIP para ${pendientes.length} cliente(s)?`)) return;
    setGenerandoTodos(true);
    let ok = 0;
    for (const c of pendientes) {
      if (await generarPara(c, { silent: true })) ok++;
    }
    setGenerandoTodos(false);
    toast.success(`${ok} de ${pendientes.length} carnets generados`);
  };

  const cargarCarnets = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await axios.get(`${API}/vip/carnets`);
      setAllCarnets(data.filter(c => c.activo));
    } catch {
      toast.error('Error al cargar la lista de carnets');
    } finally {
      setLoadingList(false);
    }
  }, []);

  const cargarConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/vip/config`);
      setConfig({ codigos_excluidos: [], ...data });
      setExcluidosTexto((data.codigos_excluidos || []).join('\n'));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    cargarConfig();
    if (tab === 'carnets') cargarCarnets();
  }, [tab, cargarConfig, cargarCarnets]);

  const buscar = async (e) => {
    e?.preventDefault();
    if (!cedula.trim()) return;
    setSearching(true);
    setFrecResult(null);
    try {
      const { data } = await axios.get(`${API}/vip/frecuencia/${cedula.trim()}`);
      setFrecResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error consultando ERP');
    } finally {
      setSearching(false);
    }
  };

  const generarCarnet = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/vip/generar/${cedula.trim()}`);
      setFrecResult(prev => ({ ...prev, tiene_carnet_activo: true, carnet: data }));
      toast.success('¡Carnet VIP generado exitosamente!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error generando carnet');
    } finally {
      setGenerating(false);
    }
  };

  const revocarCarnet = async (carnetId, nombre) => {
    if (!window.confirm(`¿Revocar el carnet VIP de ${nombre}?`)) return;
    try {
      await axios.delete(`${API}/vip/carnet/${carnetId}`);
      toast.success('Carnet revocado');
      cargarCarnets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error revocando carnet');
    }
  };

  const guardarConfig = async () => {
    setSavingConfig(true);
    try {
      const codigos_excluidos = excluidosTexto.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
      await axios.post(`${API}/vip/config`, { ...config, codigos_excluidos });
      setConfig(c => ({ ...c, codigos_excluidos }));
      toast.success('Configuración VIP guardada');
      setShowConfig(false);
      if (scan) escanear();
    } catch {
      toast.error('Error guardando configuración');
    } finally {
      setSavingConfig(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-700 to-red-900 flex items-center justify-center shadow">
            <Award className="w-5 h-5 text-yellow-300" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Lealtad VIP</h1>
            <p className="text-sm text-slate-500">
              Premia a los clientes más frecuentes con el Carnet VIP Farmavida
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 text-slate-600"
          onClick={() => setShowConfig(v => !v)}
        >
          <Settings2 className="w-4 h-4" />
          Configuración
        </Button>
      </div>

      {/* Config panel */}
      {showConfig && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
          <h2 className="font-semibold text-slate-700 text-sm">Parámetros del programa VIP</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-slate-500 font-medium block mb-1">Compras mínimas / mes</label>
              <Input
                type="number" min={1}
                value={config.compras_minimas_mes}
                onChange={e => setConfig(c => ({ ...c, compras_minimas_mes: parseInt(e.target.value) || 1 }))}
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 font-medium block mb-1">Descuento (%)</label>
              <Input
                type="number" min={1} max={99} step={0.5}
                value={config.porcentaje_descuento}
                onChange={e => setConfig(c => ({ ...c, porcentaje_descuento: parseFloat(e.target.value) || 1 }))}
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 font-medium block mb-1">Vigencia (meses)</label>
              <Input
                type="number" min={1}
                value={config.meses_vigencia}
                onChange={e => setConfig(c => ({ ...c, meses_vigencia: parseInt(e.target.value) || 1 }))}
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium block mb-1">
              Códigos excluidos (códigos de caja, empleados, cuentas internas · uno por línea)
            </label>
            <textarea
              rows={5}
              value={excluidosTexto}
              onChange={e => setExcluidosTexto(e.target.value)}
              placeholder={'0735863\n16476307\n…'}
              className="w-full font-mono text-sm rounded-md border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-200"
            />
            <p className="text-xs text-slate-400 mt-1">
              Estos códigos no aparecen en el escaneo ni pueden recibir carnet.
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={guardarConfig} disabled={savingConfig} className="bg-red-700 hover:bg-red-800 text-white">
              {savingConfig ? 'Guardando…' : 'Guardar'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowConfig(false)}>Cancelar</Button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {[
          { key: 'consultar',  label: 'Consultar / Generar', Icon: Search },
          { key: 'candidatos', label: 'Candidatos VIP',      Icon: Sparkles },
          { key: 'carnets',    label: 'Todos los Carnets',   Icon: Users },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              tab === key
                ? 'bg-white text-red-700 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ── TAB: Consultar ── */}
      {tab === 'consultar' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: search */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-5">
            <h2 className="font-semibold text-slate-700">Consultar cliente por cédula</h2>

            <form onSubmit={buscar} className="flex gap-2">
              <Input
                placeholder="Número de cédula…"
                value={cedula}
                onChange={e => setCedula(e.target.value)}
                className="flex-1"
              />
              <Button
                type="submit"
                disabled={searching || !cedula.trim()}
                className="bg-red-700 hover:bg-red-800 text-white gap-2"
              >
                {searching
                  ? <RefreshCw className="w-4 h-4 animate-spin" />
                  : <Search className="w-4 h-4" />}
                Buscar
              </Button>
            </form>

            {/* Result */}
            {frecResult && (
              <div className="space-y-4">
                {/* Error de conexión ERP */}
                {frecResult.error && (
                  <div className="flex items-start gap-2 text-red-600 bg-red-50 rounded-lg p-3 text-sm border border-red-100">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>Error al conectar con el ERP: <strong>{frecResult.error}</strong></span>
                  </div>
                )}

                {/* Nombre del cliente (viene del ERP) */}
                {!frecResult.error && (
                  frecResult.cliente_nombre ? (
                    <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3 border border-slate-100">
                      <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-red-700">
                          {frecResult.cliente_nombre.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">{frecResult.cliente_nombre}</p>
                        <p className="text-xs text-slate-400">
                          C.I. {frecResult.cedula}
                          {frecResult.telefono
                            ? <> · <Phone className="w-3 h-3 inline -mt-0.5" /> {fmtTel(frecResult.telefono)}</>
                            : <span className="ml-2 text-amber-600">· sin teléfono en Tecnodroid</span>}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-700 bg-amber-50 rounded-lg p-3 text-sm border border-amber-100">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      Esta cédula no aparece en ninguna de las sedes del Tecnodroid.
                    </div>
                  )
                )}

                {/* Código excluido */}
                {!frecResult.error && frecResult.excluido && (
                  <div className="flex items-center gap-2 text-red-700 bg-red-50 rounded-lg p-3 text-sm border border-red-100">
                    <XCircle className="w-4 h-4 flex-shrink-0" />
                    Este código está en la lista de excluidos (código de caja o cuenta interna). No puede recibir carnet VIP.
                  </div>
                )}

                {/* Barra de frecuencia */}
                {!frecResult.error && (
                  <FrequencyMeter
                    compras={frecResult.compras_mes}
                    minimas={frecResult.compras_minimas_mes}
                  />
                )}

                {/* Desglose por sede */}
                {!frecResult.error && frecResult.por_sede?.length > 0 && (
                  <div className="rounded-lg border border-slate-100 overflow-hidden">
                    <div className="flex justify-between px-3 py-2 bg-slate-50 text-xs text-slate-500">
                      <span>Sede</span>
                      <span>Compras · última</span>
                    </div>
                    {frecResult.por_sede.map(s => (
                      <div key={s.sede} className="flex justify-between px-3 py-2 text-sm border-t border-slate-50">
                        <span className="text-slate-700">{s.sede}</span>
                        <span className="text-slate-500">
                          <strong className="text-slate-800">{s.compras}</strong>
                          {s.ultima && <span className="ml-2 text-xs">{fmt(s.ultima.slice(0, 10))}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Badge de elegibilidad */}
                {!frecResult.error && (
                  frecResult.elegible ? (
                    <div className="flex items-center gap-2 bg-green-50 text-green-700 rounded-lg p-3 text-sm border border-green-100">
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      <span>
                        <strong>¡Elegible!</strong> Este cliente alcanzó{' '}
                        <strong>{frecResult.compras_mes} compras</strong> este mes.
                      </span>
                    </div>
                  ) : (
                    frecResult.compras_mes > 0 && (
                      <div className="flex items-center gap-2 bg-slate-50 text-slate-500 rounded-lg p-3 text-sm border border-slate-100">
                        <XCircle className="w-4 h-4 flex-shrink-0" />
                        No alcanza el mínimo de {frecResult.compras_minimas_mes} compras en los últimos 30 días.
                      </div>
                    )
                  )
                )}

                {/* Excepción: solo usuarios autorizados, cuando no alcanza el mínimo */}
                {!frecResult.error && !frecResult.elegible && !frecResult.excluido
                  && frecResult.cliente_nombre && me?.puede_excepcion_vip && (
                  <Button
                    onClick={generarPorExcepcion}
                    disabled={generating}
                    variant="outline"
                    className="w-full gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    {generating
                      ? <><RefreshCw className="w-4 h-4 animate-spin" /> Generando…</>
                      : <><Star className="w-4 h-4" /> {frecResult.tiene_carnet_activo ? 'Renovar' : 'Generar'} carnet por excepción</>}
                  </Button>
                )}

                {/* Botón generar / renovar */}
                {!frecResult.error && frecResult.elegible && frecResult.cliente_nombre && (
                  <Button
                    onClick={generarCarnet}
                    disabled={generating}
                    className="w-full bg-gradient-to-r from-red-700 to-red-900 hover:from-red-800 hover:to-red-950 text-white gap-2"
                  >
                    {generating ? (
                      <><RefreshCw className="w-4 h-4 animate-spin" /> Generando…</>
                    ) : frecResult.tiene_carnet_activo ? (
                      <><RefreshCw className="w-4 h-4" /> Renovar Carnet VIP</>
                    ) : (
                      <><Star className="w-4 h-4" /> Generar Carnet VIP</>
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Right: card display */}
          <div className="flex flex-col items-center justify-start">
            {frecResult?.carnet ? (
              <VIPCard carnet={frecResult.carnet} />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-slate-300 gap-3 pt-10">
                <Award className="w-16 h-16" />
                <p className="text-sm text-slate-400">
                  El carnet aparecerá aquí al consultar o generar
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB: Candidatos VIP (escaneo masivo) ── */}
      {tab === 'candidatos' && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="flex items-center justify-between gap-4 p-4 border-b border-slate-100 flex-wrap">
            <div>
              <h2 className="font-semibold text-slate-700">Clientes que califican para el carnet VIP</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Escanea las ventas de todas las sedes del Tecnodroid en los últimos 30 días
                {scan && (
                  <> · {scan.clientes_analizados.toLocaleString()} clientes analizados en {scan.sedes.length} sedes
                     · mínimo {scan.compras_minimas_mes} compras · desde {fmt(scan.desde)}
                     {scan.codigos_excluidos?.length > 0 && <> · {scan.codigos_excluidos.length} códigos excluidos</>}</>
                )}
              </p>
            </div>
            <div className="flex gap-2">
              {scan && scan.candidatos.some(c => !c.tiene_carnet_activo && c.nombre) && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={generandoTodos}
                  onClick={generarTodos}
                  className="gap-2 border-red-200 text-red-700 hover:bg-red-50"
                >
                  {generandoTodos ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
                  Generar todos los pendientes ({scan.candidatos.filter(c => !c.tiene_carnet_activo && c.nombre).length})
                </Button>
              )}
              <Button
                size="sm"
                onClick={escanear}
                disabled={scanning}
                className="bg-red-700 hover:bg-red-800 text-white gap-2"
              >
                {scanning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {scanning ? 'Escaneando…' : scan ? 'Volver a escanear' : 'Escanear Tecnodroid'}
              </Button>
            </div>
          </div>

          {!scan ? (
            <div className="p-10 text-center text-slate-400 text-sm flex flex-col items-center gap-3">
              <Sparkles className="w-10 h-10 text-slate-200" />
              Presiona <strong className="text-slate-500">Escanear Tecnodroid</strong> para buscar automáticamente
              a todos los clientes con {config.compras_minimas_mes} o más compras.
            </div>
          ) : scan.candidatos.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm">
              Ningún cliente alcanza las {scan.compras_minimas_mes} compras en los últimos 30 días.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3">#</th>
                    <th className="text-left px-4 py-3">Cliente</th>
                    <th className="text-left px-4 py-3">Cédula</th>
                    <th className="text-left px-4 py-3">Teléfono</th>
                    <th className="text-right px-4 py-3">Compras</th>
                    <th className="text-left px-4 py-3">Sedes</th>
                    <th className="text-left px-4 py-3">Última compra</th>
                    <th className="text-left px-4 py-3">Carnet</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {scan.candidatos.map((c, i) => {
                    const busy = generandoCed.has(c.cedula);
                    return (
                      <tr key={c.cedula} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 text-slate-400 text-xs">{i + 1}</td>
                        <td className="px-4 py-3 font-medium text-slate-800">
                          {c.nombre || <span className="text-amber-600 text-xs">Sin nombre en ERP</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-500 font-mono text-xs">{c.cedula}</td>
                        <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">
                          {c.telefono ? fmtTel(c.telefono) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="inline-block min-w-[2.5rem] text-center font-bold text-red-700 bg-red-50 rounded-md px-2 py-0.5">
                            {c.compras}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {c.sedes.map(s => `${s.sede} (${s.compras})`).join(' · ')}
                        </td>
                        <td className="px-4 py-3 text-slate-500">{c.ultima ? fmt(c.ultima.slice(0, 10)) : '—'}</td>
                        <td className="px-4 py-3">
                          {c.tiene_carnet_activo ? (
                            <button
                              onClick={() => setPreview(c.carnet)}
                              className="text-xs text-green-700 bg-green-50 border border-green-100 rounded-full px-2 py-0.5 hover:bg-green-100"
                              title="Ver carnet"
                            >
                              ✓ Emitido · vence {fmt(c.carnet?.fecha_vencimiento)}
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">Pendiente</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {c.nombre && (
                            <Button
                              size="sm"
                              variant={c.tiene_carnet_activo ? 'ghost' : 'default'}
                              disabled={busy || generandoTodos}
                              onClick={() => generarPara(c)}
                              className={c.tiene_carnet_activo
                                ? 'h-7 text-xs text-slate-500'
                                : 'h-7 text-xs bg-red-700 hover:bg-red-800 text-white gap-1'}
                            >
                              {busy ? <RefreshCw className="w-3 h-3 animate-spin" /> : c.tiene_carnet_activo ? 'Renovar' : <><Star className="w-3 h-3" /> Generar</>}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Modal: vista previa del carnet ── */}
      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
        >
          <div className="bg-white rounded-2xl p-6 shadow-2xl relative" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setPreview(null)}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <VIPCard carnet={preview} showWhatsApp={preview.activo !== false} />
          </div>
        </div>
      )}

      {/* ── TAB: Todos los Carnets ── */}
      {tab === 'carnets' && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-700">
              Carnets VIP emitidos
              {allCarnets.length > 0 && (
                <span className="ml-2 text-xs text-slate-400 font-normal">
                  ({allCarnets.length} activos)
                </span>
              )}
            </h2>
            <div className="flex gap-2">
              {allCarnets.some(c => !c.telefono) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={actualizarTelefonos}
                  disabled={loadingList || actualizandoTel}
                  className="gap-2 border-red-200 text-red-700 hover:bg-red-50"
                >
                  <Phone className={`w-4 h-4 ${actualizandoTel ? 'animate-pulse' : ''}`} />
                  Completar teléfonos ({allCarnets.filter(c => !c.telefono).length})
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={cargarCarnets}
                disabled={loadingList}
                className="gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loadingList ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
          </div>

          {loadingList ? (
            <div className="p-8 text-center text-slate-400 text-sm">Cargando…</div>
          ) : allCarnets.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">
              No hay carnets emitidos aún
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3">Cliente</th>
                    <th className="text-left px-4 py-3">Cédula</th>
                    <th className="text-left px-4 py-3">Teléfono</th>
                    <th className="text-left px-4 py-3">N/S</th>
                    <th className="text-left px-4 py-3">Emisión</th>
                    <th className="text-left px-4 py-3">Vencimiento</th>
                    <th className="text-left px-4 py-3">Desc.</th>
                    <th className="text-left px-4 py-3">Estado</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {allCarnets.map(c => {
                    const vence = isVencido(c.fecha_vencimiento);
                    return (
                      <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-slate-800">
                          <button
                            onClick={() => setPreview(c)}
                            className="hover:text-red-700 hover:underline text-left"
                            title="Ver carnet"
                          >
                            {c.nombre_completo}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-slate-500">{c.cedula}</td>
                        <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">
                          {c.telefono ? fmtTel(c.telefono) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-600">{c.numero_serie}</td>
                        <td className="px-4 py-3 text-slate-500">{fmt(c.fecha_emision)}</td>
                        <td className={`px-4 py-3 font-medium ${vence && c.activo ? 'text-red-600' : 'text-slate-700'}`}>
                          {fmt(c.fecha_vencimiento)}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{c.porcentaje_descuento}%</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 flex-wrap">
                            {!c.activo ? (
                              <Badge variant="secondary" className="bg-slate-100 text-slate-500">Revocado</Badge>
                            ) : vence ? (
                              <Badge variant="secondary" className="bg-red-50 text-red-600">Vencido</Badge>
                            ) : (
                              <Badge className="bg-green-50 text-green-700 border-green-100">Activo</Badge>
                            )}
                            {c.excepcion && (
                              <Badge
                                variant="secondary"
                                className="bg-amber-50 text-amber-700 border-amber-100 cursor-help"
                                title={`Excepción aprobada por ${c.aprobado_por || '—'}${c.motivo ? `: ${c.motivo}` : ''}`}
                              >
                                Excepción
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {c.activo && !vence && (
                              <>
                                <Button
                                  size="sm"
                                  className="h-7 text-xs gap-1 bg-[#25D366] hover:bg-[#1ebe5b] text-white"
                                  onClick={() => copiarMensaje(c)}
                                  title="Copiar el mensaje de bienvenida VIP para pegarlo en WhatsApp"
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                  Copiar mensaje
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 w-7 p-0 border-[#25D366] text-[#25D366] hover:bg-green-50"
                                  onClick={() => abrirWhatsApp(c)}
                                  title={c.telefono ? `Abrir chat de WhatsApp con ${fmtTel(c.telefono)}` : 'Abrir WhatsApp'}
                                >
                                  <WhatsAppIcon className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs gap-1 border-red-200 text-red-700 hover:bg-red-50"
                              onClick={() => setPreview(c)}
                              title="Ver carnet"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              Ver
                            </Button>
                            {c.activo && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                                onClick={() => revocarCarnet(c.id, c.nombre_completo)}
                                title="Revocar carnet"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
